import { LOGO_RATIO } from './logo';
import { type EngraveFont } from './fonts';
import {
  canvasMeasure, displayText, layoutLogo, layoutText, logoOutline, sharedMeasureContext, slotFont, textOutline, zoneBox, zoneCoords,
  type FontMetrics, type LogoLayout, type TextLayout, type TextRoom, type ZoneBox,
} from './layout';
import { originAfterLogo, isEditable, slotOf, type EngraveState, type SlotKey, type TempleFace, type TempleSide } from './state';
import { onTemple, type TempleImage } from './templeImage';
import { TEMPLE_HINGES, TEMPLE_INFO, templeMmPerPx, zoneFor } from './zones';

/**
 * A110Z3 — everything about one temple view, computed once and shared by the card (SVG), the order sheet
 * (canvas) and the session: which slot it shows, the framing, the zone, the hinge, the text or the logo, and
 * whether a text placed by hand stays on the temple and away from the hinge.
 *
 * Geometry: the photo has its hinge on the right. A left temple seen from inside and a right temple seen from
 * outside have their hinge on the left, so the photo is mirrored for them (`mirrored`). The view frames the
 * straight part of the temple from the hinge (`VIEW_RATIO` wide for its useful thickness); the far end fades.
 */
export interface SceneInput {
  templeId: string;
  side: TempleSide;
  face: TempleFace;
  image: TempleImage | null;
  state: EngraveState;
  /** Card aspect ratio (width / height of the drawing). */
  ratio?: number;
  metrics?: (font: EngraveFont) => FontMetrics;
}

export type PlacementIssue = 'overflow' | 'hinge';

export interface Scene {
  slot: SlotKey;
  mirrored: boolean;
  W: number;
  H: number;
  mmPerPx: number;
  box: ZoneBox;
  /** Framing (viewBox) in image pixels. */
  view: { x: number; y: number; w: number; h: number };
  hinge: HingePlacement | null;
  logo: LogoLayout | null;
  text: TextLayout | null;
  /** Suffix glyph layout; text holds the full protected group for movement and collision checks. */
  caption?: TextLayout;
  font: EngraveFont;
  /** The text as drawn (capitals for an all-caps face). */
  shown: string;
  issue: PlacementIssue | null;
  /** A201: where an automatic text may run past its zone (on the temple, off the hinge, before the fade). */
  room?: TextRoom;
}

/** Widest drawing (width / height); a thick temple gets a taller card. */
export const VIEW_RATIO = 7.2;
/** Height of the view in useful thicknesses: the whole temple section with a little air. */
const VIEW_THICKNESS = 1.9;
/** Part of the temple shown from the hinge: the zone and a little beyond, within these bounds (fraction of the length). */
const SPAN_MIN = 0.6;
const SPAN_MAX = 0.78;
const SPAN_BEYOND_ZONE = 0.8;
/** The hinge end stays visible: margin before it (fraction of the view width). */
const HINGE_MARGIN = 0.025;
/** Where the far end starts fading (fraction of the view width). */
export const FADE_FROM = 0.62;
/** A text placed by hand keeps this far inside the temple's edges (mm). */
export const EDGE_MARGIN_MM = 0.3;
/** …and this far from the hinge plate and knuckle (mm). */
export const HINGE_CLEAR_MM = 0.6;

export function mirroredFor(side: TempleSide, face: TempleFace): boolean {
  return (side === 'r') !== (face === 'int');
}

// ------------------------------------------------------------ hinge (inner view)

/**
 * A201 (David, 11 octobre : « les charnières ne sont toujours pas réalistes, je t'ai donné une photo ») — the riveted
 * nickel hinge of his photo, modelled in 3D and rendered once from above as it sits inside the temple
 * (tools/engrave/hinge-render, assets-source/hinge/hinge-nickel.webp): a thick polished plate with a rounded end and
 * two rivets, a barrel of six rings on its pin. Its geometry is in units of the plate's height (`HINGE_ART`), scaled
 * to each temple.
 */
export const HINGE_ART = {
  /** The rendered picture's extent around the barrel's axis (u along the temple, v down), in plate heights. */
  ext: { u0: -0.42, u1: 2.12, v0: -0.72, v1: 0.74 },
  /** Barrel radius and length, plate tip (from the axis), pin standing out of the barrel. */
  R: 0.27, barrelLen: 1.04, plateTip: 2.0, pinOut: 0.1,
} as const;

/** Hinge proportions (mm) on the temple. The plate stops before the engraving zone (which starts ≥ 9 mm from the end). */
export const HINGE = {
  /** Plate height across the temple: at most this, and half the temple. */
  plateH: 3.4,
  /** The barrel's axis this far from the temple end (the barrel stands out past the end). */
  axis: 0.15,
  /** Room left between the plate's tip and the engraving zone. */
  plateGap: 1.6,
  /** The hinge sits a little above the temple's middle (where the core runs). */
  lift: 0.1,
} as const;

export interface HingePlacement {
  /** Transform from the hinge's mm frame (u from the temple end towards the tip, v down) to image pixels. */
  transform: string;
  /** The same as numbers: end point (px), angle (deg), x scale (±px per mm), y scale (px per mm). */
  x: number;
  y: number;
  angle: number;
  sx: number;
  sy: number;
  /** Plate height (mm): the scale of the rendered hinge. */
  plateH: number;
  /** Plate length from the temple end to its rounded tip (mm). */
  plateLen: number;
  /** Barrel length, the pin included (mm). */
  knuckleH: number;
  /** The barrel's axis, from the temple end (mm). */
  axis: number;
  /** Centre of the hinge across the temple (mm, negative = up). */
  v0: number;
}

/**
 * Where the hinge goes on the displayed (possibly mirrored) temple image, as a transform from the hinge's
 * mm frame to image pixels. Null when the temple was not measured.
 */
export function placeHinge(templeId: string, W: number, H: number, mirrored: boolean, mmPerPx: number): HingePlacement | null {
  const m = TEMPLE_HINGES[templeId];
  const info = TEMPLE_INFO[templeId];
  if (!m || !(mmPerPx > 0)) return null;
  const k = 1 / mmPerPx;
  const thickMm = m.h * H * mmPerPx;
  if (!(thickMm > 1)) return null;
  const rad = (m.angle * Math.PI) / 180;
  // Centre line at the very end (measured 5 mm before it).
  const yEnd = m.y * H + 5 * k * Math.tan(rad);
  const xEnd = mirrored ? W - m.end * W : m.end * W;
  const angle = mirrored ? -m.angle : m.angle;
  const zoneStart = info?.zoneStartMm ?? 9;
  const A = HINGE_ART;
  // One scale for the whole hinge: half the temple at most, the barrel and its pin within the temple, the plate
  // ending before the engraving zone.
  const plateH = Math.max(0.8, Math.min(HINGE.plateH, thickMm * 0.5, (thickMm * 0.82) / (A.barrelLen + A.pinOut), (zoneStart - HINGE.plateGap - HINGE.axis) / A.plateTip));
  const knuckleH = plateH * (A.barrelLen + A.pinOut);
  const plateLen = HINGE.axis + plateH * A.plateTip;
  // A little above the middle, the barrel kept inside the temple's edges.
  const room = Math.max(0, thickMm / 2 - knuckleH / 2 - 0.3);
  const v0 = -Math.min(room, thickMm * HINGE.lift);
  // u runs away from the hinge: towards −x when the hinge is on the right.
  const sx = mirrored ? k : -k;
  return {
    transform: `translate(${xEnd.toFixed(2)} ${yEnd.toFixed(2)}) rotate(${angle.toFixed(2)}) scale(${sx.toFixed(4)} ${k.toFixed(4)})`,
    x: xEnd, y: yEnd, angle, sx, sy: k, plateH, plateLen, knuckleH, axis: HINGE.axis, v0,
  };
}

/** The rendered hinge's box in the hinge's mm frame (where the picture goes). */
export function hingeImageBox(p: Pick<HingePlacement, 'plateH' | 'axis' | 'v0'>): { x: number; y: number; w: number; h: number } {
  const e = HINGE_ART.ext, s = p.plateH;
  return { x: p.axis + e.u0 * s, y: p.v0 + e.v0 * s, w: (e.u1 - e.u0) * s, h: (e.v1 - e.v0) * s };
}

/** Image pixels → the hinge's mm frame. */
function hingeCoords(h: HingePlacement, x: number, y: number): { u: number; v: number } {
  const rad = (h.angle * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  const dx = x - h.x, dy = y - h.y;
  // Undo rotate(angle) then scale(sx, sy).
  const rx = dx * c + dy * s, ry = -dx * s + dy * c;
  return { u: rx / h.sx, v: ry / h.sy };
}

/** A point inside the hinge (plate or barrel), with `HINGE_CLEAR_MM` around it. */
export function onHinge(h: HingePlacement, x: number, y: number): boolean {
  const { u, v } = hingeCoords(h, x, y);
  const halfH = Math.max(h.plateH, h.knuckleH) / 2 + HINGE_CLEAR_MM;
  return u >= h.axis - HINGE_ART.R * h.plateH - HINGE_CLEAR_MM && u <= h.plateLen + HINGE_CLEAR_MM && Math.abs(v - h.v0) <= halfH;
}

// ------------------------------------------------------------ scene

export function templeScene(input: SceneInput): Scene {
  const { templeId, side, face, image: img, state } = input;
  const ratio = input.ratio ?? VIEW_RATIO;
  const slot = slotOf(side, face);
  const mirrored = mirroredFor(side, face);
  const zone = zoneFor(templeId);
  const info = TEMPLE_INFO[templeId];
  const W = img?.width || info?.w || 1000, H = img?.height || info?.h || 300;
  const mmPerPx = templeMmPerPx(templeId, W);
  const box = zoneBox(zone, W, H, mirrored, mmPerPx, info?.zoneStartMm ?? 9);
  // View: from the hinge over the straight part (the zone and a little beyond), tall enough for the whole
  // temple section around the zone; the card takes the resulting aspect ratio (at most `ratio`).
  const span = Math.min(SPAN_MAX, Math.max(SPAN_MIN, 1 - zone.x + zone.w * SPAN_BEYOND_ZONE));
  const vw = Math.max(1, W * span);
  const vh = Math.max(vw / ratio, box.thick * VIEW_THICKNESS);
  const hingeX = mirrored ? 0 : W;
  const x0 = mirrored ? hingeX - vw * HINGE_MARGIN : hingeX + vw * HINGE_MARGIN - vw;
  const view = { x: x0, y: box.cy - vh / 2, w: vw, h: vh };
  const hinge = face === 'int' && img ? placeHinge(templeId, W, H, mirrored, mmPerPx) : null;
  const font = slotFont(state, slot);
  let text: TextLayout | null = null;
  let logo: LogoLayout | null = null;
  let caption: TextLayout | undefined;
  let shown = '';
  let issue: PlacementIssue | null = null;
  let room: TextRoom | undefined;
  if (slot === 'il') {
    logo = layoutLogo(box, state.logo);
    if (originAfterLogo(state)) {
      shown = displayText(originAfterLogo(state), font);
      const metrics = input.metrics ? input.metrics(font) : canvasMeasure(sharedMeasureContext(), font);
      const groupMetrics = { ...metrics, measure: (value: string, size: number) =>
        metrics.measure(value, size) + size * metrics.capRatio * (LOGO_RATIO + 0.8) };
      room = textRoom(img, W, H, mirrored, mmPerPx, hinge, view);
      text = layoutText(box, font, shown, state.logo, groupMetrics, room)!;
      const logoWidth = text.capPx * LOGO_RATIO;
      const suffixWidth = metrics.measure(shown, text.fontSize);
      const offset = (text.width - suffixWidth) / 2;
      const rad = text.angleDeg * Math.PI / 180;
      caption = { ...text, width: suffixWidth, cx: text.cx + offset * Math.cos(rad), cy: text.cy + offset * Math.sin(rad) };
      logo = { x: text.cx - text.width / 2, y: text.cy - text.capPx / 2,
        width: logoWidth, height: text.capPx, cx: text.cx, cy: text.cy, angleDeg: text.angleDeg };
    }
    if (state.logo.place) {
      const margin = EDGE_MARGIN_MM / mmPerPx;
      const pts = text ? textOutline(text) : logoOutline(logo);
      if (img?.outline && pts.some(p => !onTemple(img.outline, W, H, mirrored, p.x, p.y, margin))) issue = 'overflow';
      else if (hinge && pts.some(p => onHinge(hinge, p.x, p.y))) issue = 'hinge';
    }
  } else if (isEditable(slot)) {
    const raw = state.texts[slot];
    shown = displayText(raw, font);
    const metrics = input.metrics ? input.metrics(font) : canvasMeasure(sharedMeasureContext(), font);
    room = textRoom(img, W, H, mirrored, mmPerPx, hinge, view);
    text = layoutText(box, font, raw, state.style[slot], metrics, room);
    if (text && text.free) {
      const margin = EDGE_MARGIN_MM / mmPerPx;
      const pts = textOutline(text);
      if (img?.outline && pts.some(p => !onTemple(img.outline, W, H, mirrored, p.x, p.y, margin))) issue = 'overflow';
      else if (hinge && pts.some(p => onHinge(hinge, p.x, p.y))) issue = 'hinge';
    }
  }
  return { slot, mirrored, W, H, mmPerPx, box, view, hinge, logo, text, caption, font, shown, issue, ...(room ? { room } : {}) };
}

/** How far into the card's fade an automatic text may run (fraction of the view, past `FADE_FROM`). */
const ROOM_INTO_FADE = 0.06;

/**
 * A201: the room of an automatic text on this view — its whole box on the temple (with `EDGE_MARGIN_MM`), off the
 * hinge, and before the far end fades out. Without the temple's outline (not loaded yet, tests): none.
 */
export function textRoom(img: TempleImage | null, W: number, H: number, mirrored: boolean, mmPerPx: number, hinge: HingePlacement | null, view: Scene['view']): TextRoom | undefined {
  const outline = img?.outline;
  if (!outline) return undefined;
  const margin = EDGE_MARGIN_MM / mmPerPx;
  const reach = view.w * Math.min(1, FADE_FROM + ROOM_INTO_FADE);
  const x0 = mirrored ? view.x : view.x + view.w - reach;
  const x1 = mirrored ? view.x + reach : view.x + view.w;
  return l => textOutline(l).every(p => p.x >= x0 && p.x <= x1 && onTemple(outline, W, H, mirrored, p.x, p.y, margin) && !(hinge && onHinge(hinge, p.x, p.y)));
}

/** Is the centre of a text placed by hand still on the temple (drag limit)? */
export function centreOnTemple(scene: Scene, x: number, y: number, image: TempleImage | null): boolean {
  if (!image?.outline) {
    // No outline: keep the centre within the view.
    return x >= scene.view.x && x <= scene.view.x + scene.view.w && y >= scene.view.y && y <= scene.view.y + scene.view.h;
  }
  return onTemple(image.outline, scene.W, scene.H, scene.mirrored, x, y, 0);
}

/** Distance (mm) along the temple from the hinge end and across it from the zone's middle line, of a point. */
export function templeMm(scene: Scene, x: number, y: number): { along: number; across: number } {
  const { u, v } = zoneCoords(scene.box, x, y);
  const hingeEnd = scene.box.hingeLeft ? -scene.box.len / 2 : scene.box.len / 2;
  const tip = scene.box.hingeLeft ? 1 : -1;
  return { along: scene.box.zoneStartMm + tip * (u - hingeEnd) * scene.mmPerPx, across: v * scene.mmPerPx };
}
